import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Check, Code, ExternalLink, LayoutTemplate, Store } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PUBLIC_APP_URL } from "@/lib/appConfig";

function MerchantWidgetGuide() {
  const { toast } = useToast();
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const appUrl = PUBLIC_APP_URL || window.location.origin;

  const themeExtensionExample = `{% schema %}
{
  "name": "Splitkart",
  "target": "body",
  "settings": [
    {
      "type": "select",
      "id": "banner_position",
      "label": "Banner position",
      "default": "bottom-left"
    }
  ]
}
{% endschema %}`;

  const legacyInstallCode = `<!-- Legacy storefront install for private stores only -->
<script
  src="${appUrl}/shakatah-cart-widget.js"
  async
  data-merchant-id="YOUR_MERCHANT_ID"
></script>
<script
  src="${appUrl}/shakatah-banner.js?storeName=YOUR_STORE_NAME&position=bottom-left"
  async
  data-merchant-id="YOUR_MERCHANT_ID"
></script>`;

  const copyToClipboard = async (text: string, section: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast({
      title: "Copied!",
      description: "Code copied to clipboard",
    });
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const CodeBlock = ({ code, section }: { code: string; section: string }) => (
    <div className="relative">
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
        <code className="text-foreground">{code}</code>
      </pre>
      <Button
        size="sm"
        variant="secondary"
        className="absolute top-2 right-2"
        onClick={() => copyToClipboard(code, section)}
      >
        {copiedSection === section ? (
          <Check className="h-4 w-4" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="text-center space-y-4">
          <Badge className="mb-2" variant="secondary">
            Shopify Setup Guide
          </Badge>
          <h1 className="text-4xl font-bold">Activate Splitkart on Your Shopify Theme</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Use the Theme App Extension path for production Shopify apps. Keep the legacy script install only for private or custom storefront deployments.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5" />
              Recommended: Theme App Extension
            </CardTitle>
            <CardDescription>
              This is the Shopify App Store-friendly installation path.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-accent/40 p-4">
              <h4 className="font-medium mb-3">Theme Editor Steps</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Install or update the Splitkart app in your Shopify admin.</li>
                <li>Go to <strong>Online Store</strong> and then <strong>Themes</strong>.</li>
                <li>Click <strong>Customize</strong> on your live theme.</li>
                <li>Open <strong>App embeds</strong> and enable <strong>Splitkart</strong>.</li>
                <li>Keep the app embed switched on and click <strong>Save</strong>.</li>
                <li>Test the same live theme on product and cart pages.</li>
              </ol>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Store className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Production URL is fixed</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  The app embed now always loads storefront assets from <code className="bg-background px-1 rounded">{appUrl}</code>, so merchants do not need to configure that manually in Theme Editor.
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">App embed ships both assets</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  The extension loads the inline split widget and the promotional banner from the same public app domain, so theme activation replaces legacy ScriptTag injection.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="theme-extension">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="theme-extension">Theme App Embed</TabsTrigger>
            <TabsTrigger value="legacy">Legacy Fallback</TabsTrigger>
          </TabsList>

          <TabsContent value="theme-extension">
            <Card>
              <CardHeader>
                <CardTitle>Extension Schema Example</CardTitle>
                <CardDescription>
                  This matches the scaffold in <code className="bg-muted px-1 rounded">extensions/shakatah-theme/blocks/shakatah-app-embed.liquid</code>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CodeBlock code={themeExtensionExample} section="theme-extension" />
                <p className="text-sm text-muted-foreground">
                  After deployment with Shopify CLI, merchants should activate the embed from Theme Editor rather than editing theme files directly.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="legacy">
            <Card>
              <CardHeader>
                <CardTitle>Legacy Script Install</CardTitle>
                <CardDescription>
                  Use this only for private apps or stores that intentionally stay on the older ScriptTag path.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CodeBlock code={legacyInstallCode} section="legacy" />
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-100">
                  Shopify requires Theme App Extensions for public app theme integrations. Do not use this snippet as your default production install path for App Store submission.
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card>
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-medium">Need help deploying the extension?</h4>
                <p className="text-sm text-muted-foreground">
                  Use the production deployment guide in this repo for Shopify CLI config, scopes, and Supabase secret setup.
                </p>
              </div>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => window.open("/contact", "_blank")}
              >
                <ExternalLink className="h-4 w-4" />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MerchantWidgetGuide;
